import React, { useMemo, useState } from "react";
import { FaFileExport } from "react-icons/fa";
import { toast } from "react-toastify";

import Modal from "../../components/Modal";
import { getPlotDisplayLabel } from "../../utils/mapMeasurements";
import {
  buildFarmMapKml,
  downloadKmlFile,
  emptyKmlSelection,
  farmMapKmlFilename,
  farmMapKmlHasGeometry,
  filterKmlExportItems,
  groupMapsForKmlExport,
  initialKmlSelection,
  isFarmFullySelected,
  isFarmPartiallySelected,
  isPlotSelected,
  selectAllKmlSelection,
  toggleFarmInKmlSelection,
  togglePlotInKmlSelection,
} from "../../utils/kmlExportUtils";

import "./FarmMapKmlExport.css";

function FarmMapKmlExport({
  farmId,
  t,
  plots = [],
  groupFarms = [],
  selectedSubFarmIds = [],
  highlightedCropIds = [],
  disabled,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selection, setSelection] = useState(emptyKmlSelection());

  const groups = useMemo(
    () => groupMapsForKmlExport(plots, groupFarms),
    [plots, groupFarms]
  );

  function openModal() {
    if (!groups.length) {
      toast.warning(t("Nenhum mapa para exportar."), {
        position: "bottom-right",
      });
      return;
    }
    setSelection(
      initialKmlSelection({
        groups,
        selectedFarmIds: selectedSubFarmIds,
        selectedPlotIds: highlightedCropIds,
      })
    );
    setIsOpen(true);
  }

  function handleExport() {
    const selected = filterKmlExportItems({
      plots,
      groupFarms,
      selection,
    });
    const kml = buildFarmMapKml({
      plots: selected.plots,
      groupFarms: selected.groupFarms,
      lines: [],
      documentName: t("Mapa da fazenda"),
    });
    if (!farmMapKmlHasGeometry(kml)) {
      toast.warning(t("Selecione uma fazenda ou pelo menos um talhão."), {
        position: "bottom-right",
      });
      return;
    }
    downloadKmlFile(kml, farmMapKmlFilename(farmId));
    setIsOpen(false);
    toast.success(t("Mapa exportado em KML."), { position: "bottom-right" });
  }

  const selectedCount = selection.plotIds.length;
  const canExport =
    selectedCount > 0 || (selection.farmIds && selection.farmIds.length > 0);

  return (
    <>
      <button
        type="button"
        className="farm-map-page__kml-export-btn"
        onClick={openModal}
        disabled={disabled}
        title={t("Exportar mapas em KML")}
      >
        <FaFileExport aria-hidden />
        <span>{t("Exportar mapas em KML")}</span>
      </button>

      {isOpen ? (
        <Modal
          isModalOpen={isOpen}
          closeModal={() => setIsOpen(false)}
          style={{
            overlay: { backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1200 },
            modal: {
              width: "min(560px, calc(100% - 32px))",
              zIndex: 1201,
            },
          }}
        >
          <div className="farm-map-kml-export">
            <h4>{t("Exportar mapas em KML")}</h4>
            <p>
              {t(
                "Selecione a fazenda para exportar todos os talhões dela, ou marque talhão por talhão."
              )}
            </p>
            <div className="farm-map-kml-export__toolbar">
              <button
                type="button"
                className="btn-link"
                onClick={() => setSelection(selectAllKmlSelection(groups))}
              >
                {t("Selecionar todos")}
              </button>
              <button
                type="button"
                className="btn-link"
                onClick={() => setSelection(emptyKmlSelection())}
              >
                {t("Limpar seleção")}
              </button>
              <span>
                {selectedCount}{" "}
                {selectedCount === 1
                  ? t("talhão selecionado")
                  : t("talhões selecionados")}
              </span>
            </div>
            <div className="farm-map-kml-export__list">
              {groups.map((group) => {
                const farm = group.farm;
                const groupKey = farm ? String(farm.id) : "outros";
                const fully = isFarmFullySelected(group, selection);
                const partial = isFarmPartiallySelected(group, selection);
                return (
                  <div key={groupKey} className="farm-map-kml-export__group">
                    <label
                      className={
                        fully || partial
                          ? "farm-map-kml-export__farm is-selected"
                          : "farm-map-kml-export__farm"
                      }
                    >
                      <input
                        type="checkbox"
                        checked={fully}
                        ref={(element) => {
                          if (element) element.indeterminate = partial;
                        }}
                        onChange={() =>
                          setSelection(toggleFarmInKmlSelection(group, selection))
                        }
                      />
                      <span>
                        {farm
                          ? farm.nome_da_fazenda
                          : t("Outros talhões")}
                        <small>
                          {group.plots.length} {t("talhão(ões)")}
                        </small>
                      </span>
                    </label>
                    {group.plots.map((plot) => (
                      <label
                        key={plot.id}
                        className="farm-map-kml-export__plot"
                      >
                        <input
                          type="checkbox"
                          checked={isPlotSelected(plot.id, selection)}
                          onChange={() =>
                            setSelection(
                              togglePlotInKmlSelection(group, plot.id, selection)
                            )
                          }
                        />
                        <span>{getPlotDisplayLabel(plot) || plot.name}</span>
                      </label>
                    ))}
                  </div>
                );
              })}
            </div>
            <div className="farm-map-kml-export__actions">
              <button
                type="button"
                className="farm-map-kml-export__cancel"
                onClick={() => setIsOpen(false)}
              >
                {t("Cancelar")}
              </button>
              <button
                type="button"
                className="farm-map-kml-export__confirm"
                onClick={handleExport}
                disabled={!canExport}
              >
                {t("Exportar")}
              </button>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

export default FarmMapKmlExport;
